import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { tap } from 'rxjs/operators'
import { SalariesDetailService } from '../salaries-detail.service'
import { GetSalariesDetail, ResetSalariesDetail } from './salaries-detail.actions'
import { SalariesDetailStateModel } from './salaries-detail.model'
import {
  ISalariesDetailResponse,
  ISalaryCalendarDay,
  ISalaryCalendarMonth,
  ISalaryCalendarWeek
} from '../../interfaces/salaries-detail.interface'

@State<SalariesDetailStateModel>({
  name: 'salariesDetail'
})
@Injectable()
export class SalariesDetailState {
  constructor(private salariesDetailService: SalariesDetailService) {}

  @Selector()
  static salariesDetail(state: SalariesDetailStateModel): ISalariesDetailResponse | undefined {
    return state.salariesDetail
  }

  @Selector()
  static calendarData(state: SalariesDetailStateModel): ISalaryCalendarMonth[] | undefined {
    const response = state.salariesDetail
    if (!response) {
      return undefined
    }

    // Build a lookup map: "YYYY-MM-DD" -> amount
    const amountMap = new Map<string, number>()
    response.dailyItems.forEach((item) => {
      const dateKey = item.date.substring(0, 10)
      amountMap.set(dateKey, item.amount)
    })

    // Collect TakeOverDates as date strings for comparison
    const takeOverDates = new Set<string>()
    response.locations.forEach((loc) => {
      if (loc.takeOverDate) {
        takeOverDates.add(loc.takeOverDate.substring(0, 10))
      }
    })

    // Determine months in the range
    const fromStr = String(response.timePeriod.from).substring(0, 10)
    const toStr = String(response.timePeriod.to).substring(0, 10)
    const fromParts = fromStr.split('-').map(Number)
    const toParts = toStr.split('-').map(Number)

    const startYear = fromParts[0]
    const startMonth = fromParts[1] - 1
    const endYear = toParts[0]
    const endMonth = toParts[1] - 1

    const months: ISalaryCalendarMonth[] = []
    let curYear = startYear
    let curMonth = startMonth

    while (curYear < endYear || (curYear === endYear && curMonth <= endMonth)) {
      const label = new Date(curYear, curMonth, 1).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      })

      const weeks = buildMonthWeeks(curYear, curMonth, amountMap, takeOverDates)
      const monthTotal = calculateMonthTotal(curYear, curMonth, amountMap)

      months.push({ year: curYear, month: curMonth, label, weeks, monthTotal })

      curMonth++
      if (curMonth > 11) {
        curMonth = 0
        curYear++
      }
    }

    return months
  }

  @Action(GetSalariesDetail, { cancelUncompleted: true })
  getSalariesDetail(ctx: StateContext<SalariesDetailStateModel>, { payload }: GetSalariesDetail) {
    ctx.patchState({ salariesDetail: undefined })
    return this.salariesDetailService.getSalariesDetail(payload).pipe(
      tap((res) => {
        ctx.patchState({ salariesDetail: res })
      })
    )
  }

  @Action(ResetSalariesDetail)
  resetSalariesDetail(ctx: StateContext<SalariesDetailStateModel>) {
    ctx.patchState({ salariesDetail: undefined })
  }
}

function calculateMonthTotal(year: number, month: number, amountMap: Map<string, number>): number {
  let total = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  for (let day = 1; day <= daysInMonth; day++) {
    const dateKey = formatDateKey(new Date(year, month, day))
    const val = amountMap.get(dateKey)
    if (val !== undefined) {
      total += val
    }
  }
  return total
}

function formatDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function buildMonthWeeks(
  year: number,
  month: number,
  amountMap: Map<string, number>,
  takeOverDates: Set<string>
): ISalaryCalendarWeek[] {
  const weeks: ISalaryCalendarWeek[] = []

  // Find the Monday on or before the 1st of the month
  const firstOfMonth = new Date(year, month, 1)
  const dayOfWeek = firstOfMonth.getDay()
  // Convert Sunday=0 to Monday-start: Mon=0, Tue=1, ..., Sun=6
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  const startDate = new Date(year, month, 1 - mondayOffset)

  // Find the last day of the month
  const lastOfMonth = new Date(year, month + 1, 0)

  const currentDate = new Date(startDate)
  const maxWeeks = 6 // A month never spans more than 6 week rows

  for (let w = 0; w < maxWeeks; w++) {
    const days: ISalaryCalendarDay[] = []
    let weekTotal = 0

    for (let i = 0; i < 7; i++) {
      const dateKey = formatDateKey(currentDate)
      const isCurrentMonth = currentDate.getMonth() === month && currentDate.getFullYear() === year
      const amount = amountMap.get(dateKey) ?? null

      if (amount !== null) {
        weekTotal += amount
      }

      days.push({
        date: new Date(currentDate),
        dayOfMonth: currentDate.getDate(),
        amount,
        isCurrentMonth,
        isTakeOverDate: takeOverDates.has(dateKey)
      })

      currentDate.setDate(currentDate.getDate() + 1)
    }

    weeks.push({ days, weekTotal })

    // Stop once we've moved past the last day of the month on a Monday boundary
    if (currentDate > lastOfMonth) {
      break
    }
  }

  return weeks
}
